import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { ClientsDto } from 'src/app/domain/models/dto/clients.dto';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FilterClientsDto } from 'src/app/domain/models/dto/filter-clients.dto';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { ResponseTableModel } from 'src/app/domain/models/response-table.model';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject, Subscription, firstValueFrom, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { BusinessPartnerDto } from 'src/app/domain/models/dto/business-partner.dto';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import { DateAdapter } from '@angular/material/core';
import { sanitizeCnpj } from 'src/app/shared/utils/cnpj.util';

@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.scss'],
})
export class ClientsComponent implements OnInit, OnDestroy {
  showTables: boolean = false;
  haveClients: boolean = false;
  showClientsError: boolean = false;
  tableDisplayedColumns: string[];
  clients: ResponseTableModel<ClientsDto[]>;
  allClients: ClientsDto[] = [];
  filteredClients: ClientsDto[] = [];
  filterForm: FormGroup;
  pageSize: number = 10;
  pageNumber: number = 0;
  totalElements: number;
  isRefreshing: boolean = false;
  isLoadingCards: boolean = false;
  isLoadingClients: boolean = false;
  currentPersonType: string = 'Pj';

  nextToExpire: number = 0;
  expired: number = 0;
  pendingDocument: number = 0;
  noLimit: number = 0;

  partners: BusinessPartnerDto[] = [];
  agencies: BusinessPartnerDto[] = [];
  agencyFilterCtrl = new FormControl('');
  filteredAgencies: ReplaySubject<BusinessPartnerDto[]> = new ReplaySubject(1);

  @ViewChild('agencySelect', { static: false }) agencySelect!: MatSelect;
  private _onDestroy = new Subject<void>();
  loadCardSubs: Subscription;

  get filter(): FilterClientsDto {
    return {
      personType: this.filterForm.value.personType,
    };
  }

  constructor(
    private readonly companyService: CorporateAccountService,
    private readonly ordersService: OrdersService,
    private formBuilder: FormBuilder,
    private dateAdapter: DateAdapter<any>
  ) {
    this.setLocale();
    this.tableDisplayedColumns = [
      'companyName',
      'agencyNumber',
      'situation',
      'spread',
      'tariff',
      'status',
      'action',
    ];

    this.filterForm = this.formBuilder.group({
      personType: ['Pj'],
      name: [''],
      document: [''],
      situation: [''],
      status: [''],
      limitType: [''],
      expireAt: [''],
      agency: [''],
    });
  }

  async ngOnInit() {
    this.ordersService.listenerUpdate.subscribe(() => {
      this.loadSummaryCards();
      this.loadClientsFromAPI(this.pageNumber, this.pageSize);
    });

    const partnersRes = await firstValueFrom(
      this.companyService
        .getPartnersList()
        .pipe(
          catchError(() =>
            of({ data: [] } as ResponseObjectModel<BusinessPartnerDto[]>)
          )
        )
    );

    this.partners = partnersRes.data ?? [];
    this.agencies = this.partners;
    this.filteredAgencies.next(this.agencies);

    this.agencyFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filterAgencies());

    this.filterForm
      .get('personType')
      ?.valueChanges.pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterForm.get('document')?.setValue('', { emitEvent: false });
      });
  }

  loadSummaryCards() {
    this.isLoadingCards = true;

    this.companyService.getCachedAccountStatusOverview().subscribe({
      next: (result) => {
        if (result.success && result.data) {
          this.nextToExpire = result.data.nextToExpire;
          this.expired = result.data.expired;
          this.pendingDocument = result.data.pendingDocument;
          this.noLimit = result.data.noLimit;
        }
      },
      error: () => {
        this.nextToExpire = 0;
        this.expired = 0;
        this.pendingDocument = 0;
        this.noLimit = 0;
      },
    });

    this.loadCardSubs = this.companyService
      .getAccountStatusOverview()
      .subscribe({
        next: (result) => {
          if (result.success && result.data) {
            this.nextToExpire = result.data.nextToExpire;
            this.expired = result.data.expired;
            this.pendingDocument = result.data.pendingDocument;
            this.noLimit = result.data.noLimit;
          }
          this.isLoadingCards = false;
        },
        error: () => {
          this.nextToExpire = 0;
          this.expired = 0;
          this.pendingDocument = 0;
          this.noLimit = 0;
          this.isLoadingCards = false;
        },
      });
  }

  private filterAgencies() {
    const term = (this.agencyFilterCtrl.value || '').toString().toLowerCase();
    if (!term) {
      this.filteredAgencies.next(this.agencies);
      return;
    }
    this.filteredAgencies.next(
      this.agencies.filter((a) => {
        const name = (a.companyName || '').toLowerCase();
        const agency = String(a.agencyNumber ?? '');
        return name.includes(term) || agency.includes(term);
      })
    );
  }

  reloadPage() {
    window.location.reload();
  }

  filterClients(
    pageNumber: number = this.pageNumber,
    pageSize: number = this.pageSize
  ) {
    const formValue = this.filterForm.value;
    const personTypeChanged = this.currentPersonType !== formValue.personType;

    if (personTypeChanged) {
      this.currentPersonType = formValue.personType;
      this.loadClientsFromAPI(pageNumber, pageSize);
    } else {
      this.applyFrontEndFilters();
      this.paginateClients(pageNumber, pageSize);
    }
  }

  private loadClientsFromAPI(pageNumber: number, pageSize: number) {
    this.haveClients = false;
    this.isLoadingClients = true;

    this.companyService.getAccountStatus(this.filter, 1, 10000).subscribe({
      next: (result) => {
        if (result.data.content.length > 0) {
          this.allClients = result.data.content;
          this.applyFrontEndFilters();
          this.paginateClients(pageNumber, pageSize);
          this.haveClients = true;
          this.showTables = true;
          this.showClientsError = false;
        } else {
          this.allClients = [];
          this.filteredClients = [];
          this.clients = null;
          this.haveClients = false;
          this.showTables = false;
          this.showClientsError = false;
        }
        this.isLoadingClients = false;
      },
      error: () => {
        this.allClients = [];
        this.filteredClients = [];
        this.clients = null;
        this.haveClients = false;
        this.showTables = false;
        this.showClientsError = true;
        this.isLoadingClients = false;
      },
    });
  }

  private applyFrontEndFilters() {
    const formValue = this.filterForm.value;

    this.filteredClients = this.allClients.filter((client) => {
      if (formValue.name && formValue.name.trim() !== '') {
        const nameMatch = client.name
          ?.toLowerCase()
          .includes(formValue.name.toLowerCase());
        if (!nameMatch) return false;
      }

      if (formValue.document && formValue.document.trim() !== '') {
        const cleanDocument = sanitizeCnpj(formValue.document);
        const clientDocument = sanitizeCnpj(client.document);
        if (!clientDocument.includes(cleanDocument)) return false;
      }

      if (formValue.situation && formValue.situation !== '') {
        const clientSituation = client.situation?.toString() || '';
        const filterSituation = formValue.situation.toString();

        // Mapeamento entre números e strings do enum
        const situationMap: { [key: string]: string[] } = {
          WAIT: ['1', 'WAIT'],
          '1': ['1', 'WAIT'],
          APPROVED: ['4', 'APPROVED'],
          '4': ['4', 'APPROVED'],
          REPROVED: ['5', 'REPROVED'],
          '5': ['5', 'REPROVED'],
          CANCELED: ['6', 'CANCELED'],
          '6': ['6', 'CANCELED'],
          IN_PROGRESS_REGISTER_MANAGEMENT: [
            '7',
            'IN_PROGRESS_REGISTER_MANAGEMENT',
          ],
          '7': ['7', 'IN_PROGRESS_REGISTER_MANAGEMENT'],
          SENDING_REGISTRATION_REGISTER_MANAGEMENT: [
            '8',
            'SENDING_REGISTRATION_REGISTER_MANAGEMENT',
          ],
          '8': ['8', 'SENDING_REGISTRATION_REGISTER_MANAGEMENT'],
          PENDING: ['9', 'PENDING'],
          '9': ['9', 'PENDING'],
          AWAITING_CONSENT: ['10', 'AWAITING_CONSENT'],
          '10': ['10', 'AWAITING_CONSENT'],
        };

        const matchValues = situationMap[filterSituation] || [filterSituation];
        if (!matchValues.includes(clientSituation)) return false;
      }

      if (formValue.status && formValue.status !== '') {
        if (!client.status || !client.status.includes(formValue.status))
          return false;
      }

      if (formValue.limitType && formValue.limitType !== '') {
        const limit = client.opLimit || client.remainingOpLimit || 0;
        if (formValue.limitType === 'Above50K' && limit <= 50000) return false;
        if (formValue.limitType === 'Below50K' && limit > 50000) return false;
      }

      if (formValue.expireAt) {
        const filterDate = new Date(formValue.expireAt);
        filterDate.setHours(0, 0, 0, 0);
        const clientExpireDate = client.expireAt
          ? new Date(client.expireAt)
          : null;
        if (clientExpireDate) {
          clientExpireDate.setHours(0, 0, 0, 0);
          if (clientExpireDate.getTime() !== filterDate.getTime()) return false;
        } else {
          return false;
        }
      }

      if (formValue.agency && formValue.agency !== '') {
        if (client.businessPartner?.agencyNumber !== formValue.agency)
          return false;
      }

      return true;
    });

    this.totalElements = this.filteredClients.length;
  }

  paginateClients(pageNumber: number, pageSize: number) {
    const startIndex = pageNumber * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedContent = this.filteredClients.slice(startIndex, endIndex);

    this.clients = {
      content: paginatedContent,
      totalElements: this.totalElements,
      totalPages: Math.ceil(this.totalElements / pageSize),
      number: pageNumber,
      size: pageSize,
    } as ResponseTableModel<ClientsDto[]>;
  }

  refreshData() {
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    this.loadSummaryCards();
    this.pageNumber = 0;
    this.currentPersonType = this.filterForm.value.personType;
    this.loadClientsFromAPI(this.pageNumber, this.pageSize);

    setTimeout(() => {
      this.isRefreshing = false;
    }, 100);
  }

  filterByStatus(status: string) {
    this.filterForm.patchValue({ status });
    this.pageNumber = 0;
    this.applyFrontEndFilters();
    this.paginateClients(this.pageNumber, this.pageSize);
  }

  updatePageData(event: { pageNumber: number; pageSize: number }) {
    this.pageNumber = event.pageNumber;
    this.pageSize = event.pageSize;
    this.paginateClients(this.pageNumber, this.pageSize);
  }

  resetFilter() {
    this.filterForm.reset({
      personType: 'Pj',
      name: '',
      document: '',
      situation: '',
      status: '',
      limitType: '',
      expireAt: '',
      agency: '',
    });
  }

  setLocale() {
    this.dateAdapter.setLocale('pt-BR');
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.loadCardSubs?.unsubscribe();
  }
}
